import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ScheduleDialog from "@/components/app/ScheduleDialog";
import ScheduleWeekDialog from "@/components/app/ScheduleWeekDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles, Copy, Hash, MessageSquare, Linkedin, Twitter, Instagram,
  Facebook, AtSign, Cloud, FileText, Globe2, KeyRound, Share2, AlertCircle, CalendarClock, CalendarPlus,
} from "lucide-react";

const PLATFORM_META = {
  linkedin:  { name: "LinkedIn",  icon: Linkedin,    accent: "text-[#0a66c2]" },
  x:         { name: "X",         icon: Twitter,     accent: "text-foreground" },
  instagram: { name: "Instagram", icon: Instagram,   accent: "text-[#d6249f]" },
  facebook:  { name: "Facebook",  icon: Facebook,    accent: "text-[#1877f2]" },
  threads:   { name: "Threads",   icon: AtSign,      accent: "text-foreground" },
  bluesky:   { name: "Bluesky",   icon: Cloud,       accent: "text-[#0085ff]" },
};

const ALL_PLATFORMS = ["linkedin", "x", "instagram", "facebook", "threads", "bluesky"];

function PlatformChip({ id, active, onToggle }) {
  const M = PLATFORM_META[id];
  const Icon = M.icon;
  return (
    <button
      type="button"
      onClick={() => onToggle(id)}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-widest transition-colors ${
        active
          ? "bg-foreground text-background border-foreground"
          : "bg-white border-border hover:border-foreground"
      }`}
      data-testid={`social-platform-${id}`}
    >
      <Icon className={`w-3.5 h-3.5 ${active ? "" : M.accent}`} />
      {M.name}
    </button>
  );
}

function copyToClipboard(text) {
  navigator.clipboard.writeText(text)
    .then(() => toast.success("Copied"))
    .catch(() => toast.error("Copy failed"));
}

function shareIntent(platform, text, hashtags) {
  const full = (text + (hashtags?.length ? "\n\n" + hashtags.map(h => `#${h}`).join(" ") : "")).trim();
  if (platform === "x") {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(full)}`, "_blank");
    return;
  }
  if (platform === "linkedin") {
    // LinkedIn share intent requires a URL not text; we'll copy text + open compose
    copyToClipboard(full);
    window.open("https://www.linkedin.com/feed/?shareActive=true", "_blank");
    toast.info("Text copied — paste into LinkedIn composer");
    return;
  }
  if (platform === "facebook") {
    copyToClipboard(full);
    window.open("https://www.facebook.com/", "_blank");
    toast.info("Text copied — paste into Facebook composer");
    return;
  }
  // instagram / threads / bluesky have no web compose — fall back to copy
  copyToClipboard(full);
  toast.info(`Text copied — paste into ${PLATFORM_META[platform].name}`);
}

function VariantCard({ platform, idx, variant, onSchedule }) {
  const over = variant.char_count > variant.char_limit;
  return (
    <div className="border border-border bg-white p-4 space-y-3" data-testid={`variant-card-${platform}-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
          Variant {idx + 1}
        </span>
        <span className={`font-mono-data text-[10px] uppercase tracking-widest ${over ? "text-red-700" : "text-muted-foreground"}`}>
          {variant.char_count} / {variant.char_limit} chars
        </span>
      </div>
      <pre className="whitespace-pre-wrap break-words font-sans text-sm leading-relaxed">{variant.text}</pre>
      {variant.hashtags?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-dashed border-border">
          {variant.hashtags.map((h, i) => (
            <Badge key={i} variant="outline" className="rounded-none font-mono-data text-[10px] uppercase">
              <Hash className="w-2.5 h-2.5 mr-0.5" />{h}
            </Badge>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <Button size="sm" variant="outline" className="rounded-none h-8"
          onClick={() => copyToClipboard(
            variant.text + (variant.hashtags?.length ? "\n\n" + variant.hashtags.map(h => `#${h}`).join(" ") : "")
          )}
          data-testid={`variant-copy-${platform}-${idx}`}>
          <Copy className="w-3 h-3 mr-1.5" /> Copy
        </Button>
        <Button size="sm" variant="outline" className="rounded-none h-8"
          onClick={() => shareIntent(platform, variant.text, variant.hashtags)}
          data-testid={`variant-share-${platform}-${idx}`}>
          <Share2 className="w-3 h-3 mr-1.5" /> Share
        </Button>
        {onSchedule && (
          <Button size="sm" className="rounded-none h-8 bg-foreground hover:bg-foreground/90"
            onClick={() => onSchedule(platform, variant)}
            data-testid={`variant-schedule-${platform}-${idx}`}>
            <CalendarClock className="w-3 h-3 mr-1.5" /> Schedule
          </Button>
        )}
      </div>
    </div>
  );
}

function ThreadCard({ tweets }) {
  const full = tweets.map((t, i) => `${i + 1}/${tweets.length} ${t}`).join("\n\n");
  return (
    <div className="border-2 border-foreground bg-white p-4 space-y-3" data-testid="thread-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4" />
          <h5 className="font-semibold tracking-tight text-sm">X Thread · {tweets.length} tweets</h5>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => copyToClipboard(full)} data-testid="thread-copy-all">
            <Copy className="w-3 h-3 mr-1.5" /> Copy all
          </Button>
          <Button size="sm" className="rounded-none h-8 bg-foreground hover:bg-foreground/90"
            onClick={() => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweets[0])}`, "_blank")}
            data-testid="thread-tweet-first">
            <Twitter className="w-3 h-3 mr-1.5" /> Tweet 1/{tweets.length}
          </Button>
        </div>
      </div>
      <ol className="space-y-2">
        {tweets.map((t, i) => (
          <li key={i} className="border border-dashed border-border p-3 text-sm relative" data-testid={`thread-tweet-${i}`}>
            <span className="absolute top-2 right-2 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              {i + 1}/{tweets.length} · {t.length}c
            </span>
            <p className="whitespace-pre-wrap pr-16">{t}</p>
            <div className="mt-2 flex gap-2">
              <button onClick={() => copyToClipboard(`${i + 1}/${tweets.length} ${t}`)} className="font-mono-data text-[10px] uppercase tracking-widest hover:underline" data-testid={`thread-tweet-copy-${i}`}>
                copy
              </button>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function SocialStudio({
  mode = "auth",           // "auth" | "public"
  projectId = null,        // required when mode === "auth"
  drafts = [],             // list of {id, title, target_keyword, content}
}) {
  const [platforms, setPlatforms] = useState(["linkedin", "x", "instagram"]);
  const [inputMode, setInputMode] = useState("keyword"); // keyword | content | url
  const [topic, setTopic] = useState("");
  const [content, setContent] = useState("");
  const [url, setUrl] = useState("");
  const [tone, setTone] = useState("professional");
  const [variants, setVariants] = useState(2);
  const [threadMode, setThreadMode] = useState(true);
  const [includeHashtags, setIncludeHashtags] = useState(true);
  const [selectedDraft, setSelectedDraft] = useState("");

  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  // Schedule dialog state (auth mode only)
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleInitial, setScheduleInitial] = useState({});
  const [scheduleWeekOpen, setScheduleWeekOpen] = useState(false);

  const openSchedule = (platform, variant) => {
    setScheduleInitial({
      platform,
      content: variant.text,
      hashtags: variant.hashtags || [],
      project_id: projectId || null,
    });
    setScheduleOpen(true);
  };

  const togglePlatform = (id) => {
    setPlatforms((arr) => arr.includes(id) ? arr.filter((p) => p !== id) : [...arr, id]);
  };

  const onDraftSelect = (id) => {
    setSelectedDraft(id);
    const d = drafts.find((x) => x.id === id);
    if (d) {
      setContent(d.content || "");
      if (!topic.trim()) setTopic(d.target_keyword || d.title || "");
      setInputMode("content");
      toast.success(`Loaded "${d.title}"`);
    }
  };

  const generate = async () => {
    if (platforms.length === 0) { toast.error("Pick at least one platform"); return; }
    if (inputMode === "keyword" && !topic.trim()) { toast.error("Enter a topic or keyword"); return; }
    if (inputMode === "content" && !content.trim()) { toast.error("Paste some source content"); return; }
    if (inputMode === "url" && !url.trim()) { toast.error("Enter a URL"); return; }

    setBusy(true); setResults(null);
    const endpoint = mode === "public" ? "/social/generate" : `/projects/${projectId}/social/generate`;
    try {
      const { data } = await api.post(endpoint, {
        platforms,
        input_mode: inputMode,
        topic, content, url,
        tone,
        variants_per_platform: variants,
        thread_mode: threadMode,
        include_hashtags: includeHashtags,
      });
      setResults(data.results || []);
      toast.success(`Generated posts for ${data.results?.length || 0} platform(s)`);
      setTimeout(() => {
        document.getElementById("social-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generation failed");
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6">
      {/* Brief panel */}
      <div className="border-2 border-foreground bg-white p-5 md:p-6 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />
        <div className="relative space-y-5">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            <h3 className="font-semibold tracking-tight text-lg">Social post generator</h3>
            <span className="font-mono-data text-[10px] uppercase tracking-widest text-primary border border-primary px-1.5 py-0.5">new</span>
          </div>
          <p className="text-sm text-muted-foreground -mt-3">
            Pick platforms, give a brief, and get publish-ready posts in seconds. Each variant respects the platform&apos;s tone, length and hashtag norms.
          </p>

          {/* Platforms */}
          <div>
            <Label className="font-mono-data text-xs uppercase tracking-widest">Platforms</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {ALL_PLATFORMS.map((p) => (
                <PlatformChip key={p} id={p} active={platforms.includes(p)} onToggle={togglePlatform} />
              ))}
            </div>
          </div>

          {/* Input mode tabs */}
          <div>
            <Label className="font-mono-data text-xs uppercase tracking-widest">Source</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                ["keyword", "Keyword / topic", KeyRound],
                ["content", "Paste content", FileText],
                ["url", "From URL", Globe2],
              ].map(([id, label, Icon]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setInputMode(id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-widest ${
                    inputMode === id
                      ? "bg-foreground text-background border-foreground"
                      : "bg-white border-border hover:border-foreground"
                  }`}
                  data-testid={`social-input-mode-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
          </div>

          {inputMode === "keyword" && (
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Topic / keyword</Label>
              <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                placeholder="e.g. 'how to scale a one-person SaaS in 2026'"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="social-topic" />
            </div>
          )}

          {inputMode === "content" && (
            <div className="space-y-2">
              {mode === "auth" && drafts.length > 0 && (
                <div>
                  <Label className="font-mono-data text-xs uppercase tracking-widest">Repurpose a saved draft</Label>
                  <Select value={selectedDraft} onValueChange={onDraftSelect}>
                    <SelectTrigger className="rounded-none h-10 border-foreground/30 mt-2" data-testid="social-draft-select">
                      <SelectValue placeholder="Select a draft to load…" />
                    </SelectTrigger>
                    <SelectContent className="rounded-none">
                      {drafts.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.title || "Untitled"} · {(d.content || "").split(/\s+/).filter(Boolean).length}w
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Label className="font-mono-data text-xs uppercase tracking-widest mt-3 inline-block">Source content (article, summary, notes)</Label>
              <Textarea value={content} onChange={(e) => setContent(e.target.value)}
                placeholder="Paste an article, blog post, transcript or notes…"
                className="rounded-none border-foreground/30 focus-visible:ring-foreground font-mono-data text-sm min-h-[200px]"
                data-testid="social-content" />
              <div className="space-y-2">
                <Label className="font-mono-data text-xs uppercase tracking-widest">Topic hint (optional)</Label>
                <Input value={topic} onChange={(e) => setTopic(e.target.value)}
                  placeholder="One-line angle, e.g. 'the most undervalued growth lever'"
                  className="rounded-none h-10 border-foreground/30 focus-visible:ring-foreground"
                  data-testid="social-content-topic" />
              </div>
            </div>
          )}

          {inputMode === "url" && (
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">URL to summarize into posts</Label>
              <Input value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://yourblog.com/post"
                className="rounded-none h-11 border-foreground/30 focus-visible:ring-foreground"
                data-testid="social-url" />
            </div>
          )}

          {/* Options row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Tone</Label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="social-tone"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="playful">Playful</SelectItem>
                  <SelectItem value="authoritative">Authoritative</SelectItem>
                  <SelectItem value="contrarian">Contrarian</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Variants per platform</Label>
              <Select value={String(variants)} onValueChange={(v) => setVariants(Number(v))}>
                <SelectTrigger className="rounded-none h-10 border-foreground/30" data-testid="social-variants"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="1">1</SelectItem>
                  <SelectItem value="2">2 (recommended)</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="4">4 (slower)</SelectItem>
                  <SelectItem value="5">5 (slowest)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2">
              <Label className="font-mono-data text-xs uppercase tracking-widest">Extras</Label>
              <div className="flex flex-wrap gap-2">
                <button type="button"
                  onClick={() => setIncludeHashtags((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-widest ${
                    includeHashtags ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:border-foreground"
                  }`}
                  data-testid="social-toggle-hashtags">
                  <Hash className="w-3 h-3" /> Hashtags
                </button>
                <button type="button"
                  onClick={() => setThreadMode((v) => !v)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-xs font-mono-data uppercase tracking-widest ${
                    threadMode ? "bg-foreground text-background border-foreground" : "bg-white border-border hover:border-foreground"
                  }`}
                  data-testid="social-toggle-thread">
                  <MessageSquare className="w-3 h-3" /> X Thread
                </button>
              </div>
            </div>
          </div>

          <Button onClick={generate} disabled={busy}
            className="rounded-none h-12 bg-foreground hover:bg-foreground/90 px-6"
            data-testid="social-generate">
            <Sparkles className="w-4 h-4 mr-2" />
            {busy ? "Writing posts…" : "Generate posts"}
          </Button>
          {mode === "auth" && projectId && (
            <Button onClick={() => setScheduleWeekOpen(true)} variant="outline"
              className="rounded-none h-12 px-5 ml-2 border-foreground/30"
              data-testid="social-schedule-week-btn">
              <CalendarPlus className="w-4 h-4 mr-2" /> Auto-schedule a week
            </Button>
          )}
          {busy && (
            <p className="font-mono-data text-[11px] uppercase tracking-widest text-primary" data-testid="social-busy">
              › Drafting {platforms.length} platform(s) × {variants} variant(s)… typically 15–45s
            </p>
          )}
        </div>
      </div>

      {/* Results */}
      {results && results.length > 0 && (
        <div id="social-results" className="space-y-6" data-testid="social-results">
          {results.map((r) => {
            const M = PLATFORM_META[r.platform];
            const Icon = M.icon;
            return (
              <section key={r.platform} className="space-y-3" data-testid={`social-result-${r.platform}`}>
                <div className="flex items-center justify-between flex-wrap gap-2 border-b border-border pb-2">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${M.accent}`} />
                    <h4 className="font-semibold tracking-tight text-xl">{M.name}</h4>
                    <span className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                      {r.variants.length} variant{r.variants.length === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                  {r.variants.map((v, i) => (
                    <VariantCard key={i} platform={r.platform} idx={i} variant={v}
                      onSchedule={mode === "auth" ? openSchedule : null} />
                  ))}
                </div>
                {r.thread && r.thread.length > 0 && (
                  <ThreadCard tweets={r.thread} />
                )}
              </section>
            );
          })}
        </div>
      )}

      {results && results.length === 0 && (
        <div className="border border-dashed border-foreground/30 p-8 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" /> No posts returned. Try a different brief.
        </div>
      )}

      {mode === "auth" && (
        <>
        <ScheduleDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          initial={scheduleInitial}
          projects={projectId ? [{ id: projectId, name: "This project" }] : []}
          defaultProjectId={projectId}
          onSaved={() => toast.success("Added to your social calendar")}
        />
        {projectId && (
          <ScheduleWeekDialog
            open={scheduleWeekOpen}
            onOpenChange={setScheduleWeekOpen}
            projectId={projectId}
            defaultTopic={topic}
            onCompleted={(d) => toast.success(`Auto-scheduled ${d.created} posts across ${d.summary?.days_scheduled} days`)}
          />
        )}
        </>
      )}
    </div>
  );
}
